import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import axios from 'axios';
import Modal from '../../../commonComponents/modal/Modal';
import translate from '../../../commonComponents/translate';
import FormInput from '../../../commonComponents/forms/FormInput';
import UserProp from '../../../commonComponents/propTypes/userProp';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import Request from '../../../services/request';
import Notifications from '../../../services/notifications';
import withCSRF from '../../../commonComponents/withCSRF';
import {
  compose,
  findCurrencySymbol,
  formatPrice,
  handleBillingAddressInOrder,
  handleShippingAddressInOrder,
} from '../../../services/utilityFunctions';
import List from '../../../commonComponents/lists/ClientSideList';
import CurrencyProp from '../../profile/propTypes/currencyProp';
import Loader from '../../../services/loader';
import CustomerInfo from '../../orders/Components/CustomerInfo';
import { PAYMENT_PROVIDER } from '../../../commonComponents/propTypes/tenantProp';
import ConfigsContext from '../../../contextProviders/ConfigsContext';

const initialStateFooter = {
  subtotal: 0,
  shipping: 0,
  vat: 0,
  total: 0,
  symbol: '',
  discount: 0,
};

function MakeOrderModal({ t, user, onClose, _csrf, currencies }) {
  const {
    personalInfo = {},
    shippingCountry = null,
    currency = 'eur',
    _id,
  } = user;
  const [productsSelector, setProductsSelector] = useState([]);
  // State for checkbox, select a different shipping address
  const [shippingAddressIsDifferent, setShippingAddressIsDifferent] =
    useState(false);
  const loader = useRef(new Loader('#modalBody'));
  const [isLoading, setIsLoading] = useState(false);
  // Flag to alert if product is digital
  const [isOnlyDigitalProducts, setIsOnlyDigitalProducts] = useState(false);
  const { tenant } = useContext(ConfigsContext);
  const { paymentProviderType } = tenant.settings || {};
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);
  const tableId = 'orderList';
  const tableEl = useRef(null);
  const [checkTheStock, setCheckTheStock] = useState(false);
  const { loggedUser } = useContext(LoggedUserContext);
  const [productItems, setProductItems] = useState([]);
  const [newProduct, setNewProduct] = useState({
    product: null,
    amount: 0,
  });

  const [footerValues, setFooterValues] = useState(initialStateFooter);
  const [{ show: showNewProduct }, setShowNewProduct] = useState({
    show: false,
  });
  const [state, setState] = useState({ addressOption: '', providerOption: '' });
  const [providerSelected, setProviderSelected] = useState(
    state.providerOption,
  );
  const [providersList, setProvidersList] = useState([]);
  // handle coupon
  const [discountValue, setDiscountValue] = useState(0);
  const [discountCode, setDiscountCode] = useState('');
  const handleChangeCoupon = value => {
    setDiscountCode(value);
  };
  // Disable button "Place Order"
  const disabled =
    !isLoading &&
    user.address &&
    (isOnlyDigitalProducts
      ? true
      : state.providerOption.length > 0 &&
        productItems.length > 0 &&
        providersList.length > 0);

  const [disabledField, setDisabledField] = useState(true);
  // handle table of product variations
  const addProduct = () => {
    if (productItems.length > 0) {
      const found = productItems.some(el => el.product === newProduct.product);
      if (found === true) {
        const newList = productItems.map(item => {
          if (item.product === newProduct.product) {
            return {
              ...item,
              amount:
                parseInt(item.amount, 10) + parseInt(newProduct.amount, 10),
            };
          }

          return item;
        });
        tableEl.current.setList(newList);
        setProductItems(newList);
        setShowNewProduct({ show: false });
      } else {
        setProductItems(prevState => {
          const newList = [...prevState, newProduct];
          tableEl.current.setList(newList);
          return newList;
        });
        setShowNewProduct({ show: false });
      }
    } else {
      setProductItems(prevState => {
        const newList = [...prevState, newProduct];
        tableEl.current.setList(newList);
        return newList;
      });
      setShowNewProduct({ show: false });
    }
    setDisabledField(true);
  };

  const handleChangeProduct = field => value => {
    if (field === 'product') {
      const productToAdd = productsSelector.find(p => p._id === value);
      let isDigitalContent = false;
      if (productToAdd) {
        // It is used to identify which products are digital
        isDigitalContent = productToAdd?.parentProduct
          ? productToAdd.parentProduct?.isDigital
          : productToAdd?.digitalDocument &&
            productToAdd?.digitalDocument?.url?.length > 0;
      }
      setNewProduct(prevState => ({
        ...prevState,
        amount: 1,
        isDigitalContent,
      }));
    }
    setDisabledField(false);
    setNewProduct(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (productItems.length > 0) {
      // Filter by the digital ones
      const hasDigitalInCart = productItems.filter(
        p => p.isDigitalContent === true,
      );
      // Check if all is digital by comparing the products cart length with the actually digital products in cart
      setIsOnlyDigitalProducts(hasDigitalInCart.length === productItems.length);
    }
  }, [productItems]);
  // Check if the quantity to order is lower than the stock available
  useEffect(() => {
    const product = productsSelector.find(p => p._id === newProduct?.product);
    if (product) {
      const productAlreadyExist = productItems.find(
        p => p.product === newProduct.product,
      );
      // check if product already exist, if so, add the quantity selected by user to the quantity already in list
      if (productAlreadyExist) {
        const quantityAlreadySelected =
          parseInt(newProduct?.amount, 10) +
          parseInt(productAlreadyExist.amount, 10);

        const seeIfStockIsValid =
          product?.stockQuantity - quantityAlreadySelected < 0;

        setCheckTheStock(
          product?.stockQuantity === null ? false : seeIfStockIsValid,
        );
      } else {
        setCheckTheStock(
          product?.stockQuantity === null
            ? false
            : product?.stockQuantity - parseInt(newProduct?.amount, 10) < 0,
        );
      }
    }
  }, [checkTheStock, newProduct, productItems]);

  const AddProduct = () => {
    // user don't have phone number && not have in address already saved one
    if (
      (!user?.phone || user?.phone?.length === 0) &&
      (!user?.address?.phone || user?.address?.phone?.length === 0)
    ) {
      Notifications.showNotificationError(
        t('error'),
        t('marketplace.shippingTab.error.phone'),
      );
      return;
    }
    setShowNewProduct({ show: true });
  };

  const handleChange = field => value => {
    setState(prevState => ({ ...prevState, [field]: value }));
  };
  // handle shipping provider choice
  const handleProvider = event => {
    const prov = event.target.value;
    setProviderSelected(() => {
      handleChange('providerOption')(prov);
      return prov;
    });
  };
  // handle shipping address choice
  const [shippingSelected, setShippingSelected] = useState(
    state?.addressOption?._id,
  );
  const handleShipping = value => {
    setShippingSelected(() => {
      handleChange('addressOption')(value);
      return value;
    });
  };

  // handle the table parameters
  const getImage = id => {
    const { thumb } = productsSelector.find(x => x._id === id).icon;
    const { name } = productsSelector.find(x => x._id === id);
    return (
      <div className="thumbnail-list">
        <div className="img-container">
          <img
            style={{ borderRadius: '6px' }}
            src={thumb || '/images/no_image_upload.png'}
            alt={name}
          />
        </div>
      </div>
    );
  };
  const getPrice = id => {
    const product = productsSelector.find(x => x._id === id);
    return `${formatPrice(
      Math.round(
        product?.userPromotionPrice?.price || product?.userPrice?.price,
      ),
      product?.userPromotionPrice?.symbol || product?.userPrice?.symbol,
    )}`;
  };
  const getProductName = id => {
    const { name } = productsSelector.find(x => x._id === id);
    return `${name}`;
  };
  const getVAT = id => {
    const productEntry = productsSelector.find(x => x._id === id);
    // Get the userPromotionPrice if there is one or the regular userPrice
    const userPrice =
      productEntry?.userPromotionPrice?.price >= 0
        ? productEntry?.userPromotionPrice
        : productEntry.userPrice;
    return `${userPrice.tax}%`;
  };
  const getSubTotal = (id, qty) => {
    const product = productsSelector.find(x => x._id === id);
    return `${formatPrice(
      Math.round(
        product?.userPromotionPrice?.price || product?.userPrice?.price,
      ) * qty,
      product?.userPromotionPrice?.symbol || product?.userPrice?.symbol,
    )}`;
  };

  const handleVariationRemove = index => {
    tableEl.current.removeListItem(index);
    setProductItems(tableEl.current.getList());
  };
  function handleListInitialization() {
    $(`#${tableId}`).on('click', '.remove-button', function removeHandler() {
      const index = $(this).attr('data-product-index');
      handleVariationRemove(index);
    });
  }
  const renderProductActions = index =>
    renderToStaticMarkup(
      <button
        style={{ textDecoration: 'none' }}
        type="button"
        className="btn btn-link remove-button"
        data-product-index={index}
      >
        <i className="la la-trash" />
      </button>,
    );
  const columns = [
    {
      field: 'image',
      title: '',
      textAlign: 'center',
      overflow: 'visible',
      sortable: false,
      template: p => renderToStaticMarkup(getImage(p.product)),
    },
    {
      field: 'productName',
      title: t('sessionDetails_modal.productName'),
      textAlign: 'center',
      sortable: false,
      template: p => getProductName(p.product),
    },
    {
      field: 'product',
      title: t('sessionDetails_modal.price'),
      textAlign: 'center',
      sortable: false,
      template: p => getPrice(p.product),
    },
    {
      field: 'amount',
      title: t('sessionDetails_modal.qty'),
      textAlign: 'center',
      sortable: false,
      template: p => renderToStaticMarkup(<>{p.amount}</>),
    },
    {
      field: 'vat',
      title: t('shop.order.pay.estimation.tax'),
      textAlign: 'center',
      sortable: false,
      template: p => getVAT(p.product),
    },
    {
      field: 'subtotal',
      title: t('sessionDetails_modal.subtotal'),
      textAlign: 'center',
      sortable: false,
      template: p => getSubTotal(p.product, p.amount),
    },
    {
      field: 'Actions',
      title: 'Actions',
      sortable: false,
      overflow: 'visible',
      template: (p, index) => renderProductActions(index),
    },
  ];

  // handle shipping address
  const shippingAdd = user.shippingAddress;
  const shippingObject = shippingAdd.find(x => x._id === shippingSelected?._id);

  const confirmPayment = async orderId => {
    if (paymentProviderType === PAYMENT_PROVIDER.STRIPE) {
      try {
        const response = await axios.post(
          `/api/payment/intent/market/order/${orderId}/confirm`,
          {},
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'License',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        );
        // Handle success response if needed
        console.log(response); // Log the response if needed
      } catch (err) {
        Notifications.showErrorFromRequest(err, 'order_create_fail');
        console.error(err); // Log the error for debugging
      }
    }
  };

  const createOrder = async () =>
    new Promise((resolve, reject) => {
      try {
        setIsLoading(true);
        const billingAddress = handleBillingAddressInOrder(user, user.address);
        const { businessType = null, companyName = null } =
          user?.personalInfo || {};
        if (!(businessType === 'individual' || !businessType) && companyName) {
          billingAddress.companyName = companyName;
        }
        const shippingObjectToSend = handleShippingAddressInOrder(
          user,
          shippingObject,
        );
        const orderData = {
          owner: user._id,
          shipping: !isOnlyDigitalProducts ? providerSelected : null,
          discount: discountValue.code,
          currency: user.currency,
          billingAddress,
          shippingAddress: !isOnlyDigitalProducts
            ? shippingObjectToSend || billingAddress
            : billingAddress,
          products: productItems.map(p => ({
            product: p.product,
            quantity: p.amount,
          })),
          seller: loggedUser._id,
        };
        let url = '/api/market/order?';
        if (user.shippingCountry) {
          url += `&country=${user.shippingCountry}`;
        }
        if (user.currency) {
          url += `&currency=${user.currency}`;
        }
        if (footerValues.total === 0) {
          orderData.paymentMethodType = 'bank_transfer_manual';
        }
        new Request(_csrf)
          .post(url, orderData)
          .done(async ({ data }) => {
            if (footerValues.total === 0) {
              const orderId = data._id;
              let requestURL = `/api/payment/intent/market/order/${orderId}/capture`;

              if (paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES) {
                requestURL = `/api/order/market/${orderId}/capture_payment`;
              }
              await axios
                .post(
                  requestURL,
                  {
                    paymentMethod: 'bank_transfer_manual',
                  },
                  {
                    headers: {
                      'Accept-Version': '1.0.0',
                      'X-Gamification-API': 'License',
                      'X-CSRF-Token': _csrf,
                      _csrf,
                    },
                  },
                )
                .then(async () => {
                  await confirmPayment(orderId);
                })
                .catch(err => {
                  Notifications.showErrorFromRequest(err, 'order_create_fail');
                  console.log(err);
                });
            }
            resolve(data);
            Notifications.showNotificationSuccess(
              t('success'),
              t('success_create_order'),
            );
            onClose();
            setIsLoading(false);
          })
          .fail(err => {
            Notifications.showErrorFromRequest(err, 'error_createOrder');
            setIsLoading(false);
            reject(err);
          });
      } catch (err) {
        reject(err);
        setIsLoading(false);
      }
    });
  const [errorMessage, setErrorMessage] = useState(false);
  const checkCouponCode = () => {
    const variationListToCheck = productItems.map(p => ({
      variation: p.product,
      quantity: parseInt(p.amount, 10),
    }));
    if (discountCode?.toLowerCase().trim() === 'coins') {
      setErrorMessage(true);
      setDiscountCode('');
    } else {
      setErrorMessage(false);
      axios
        .post(
          `/api/discount/coupon/market/code/${discountCode}/calculate`,
          {
            marketProductVariations: variationListToCheck,
          },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(({ data: response }) => {
          if (response.data.discount > 0) {
            setDiscountValue({
              discount: response.data.discount,
              code: discountCode,
            });
            setDiscountCode('');
          } else {
            Notifications.showErrorFromRequest(
              null,
              'error_invalidDiscountCoupon',
            );
          }
        })
        .catch(err => {
          setDiscountCode('');
          Notifications.showErrorFromRequest(
            err,
            'error_invalidDiscountCoupon',
          );
          console.log(err);
        });
    }
  };

  useEffect(() => {
    if (productItems.length !== 0) {
      const params = {
        billingAddress: user.address,
        shippingAddress: shippingObject || user.address,
        products: productItems.map(p => ({
          product: p.product,
          quantity: p.amount,
        })),
      };
      let isValid = true;
      if (
        !personalInfo?.businessType ||
        personalInfo?.businessType === 'individual'
      ) {
        // Check if address is valid according to shipping country
        isValid = shippingCountry
          ? shippingCountry === params.shippingAddress?.countryCode
          : true;
      }
      let url = '/api/market/shipping-provider/calculate-shipping?';
      if (user.shippingCountry) {
        url += `&country=${user.shippingCountry}`;
      }
      if (user.currency) {
        url += `&currency=${user.currency}`;
      }
      if (isValid) {
        axios
          .post(url, params, {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          })
          .then(({ data }) => {
            // filter so it only appears valid shipping provider
            setProvidersList(
              data.data.filter(
                f =>
                  f?.userClickAndCollectPrice?.price >= 0 ||
                  f?.userPrice?.price >= 0,
              ),
            );
          })
          .catch(err => {
            Notifications.showErrorFromRequest(err, 'error_calculateShipping');
            console.log('Err', err);
          });
      }
    }
  }, [shippingObject, productItems, shippingAddressIsDifferent]);

  /** CHECK WITH THE API THE CORRECT VALUES TO DISPLAY IN ORDER */
  const checkOrderDetail = () => {
    const params = {
      products: productItems.map(p => ({
        product: p.product,
        quantity: p.amount,
      })),
      owner: user._id,
    };
    if (user.address) {
      params.billingAddress = user.address;
    }
    if (shippingObject && shippingObject.countryCode) {
      params.shippingAddress = shippingObject;
    } else if (user.address) {
      params.shippingAddress = user.address;
    }
    if (providerSelected) {
      params.shipping = providerSelected;
    }
    if (discountValue.code) {
      params.discount = discountValue.code;
    }
    let url = '/api/market/order/calculate?populate=products.product';
    if (user.shippingCountry) {
      url += `&country=${user.shippingCountry}`;
    }
    if (user.currency) {
      url += `&currency=${user.currency}`;
    }
    axios
      .post(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      })
      .then(({ data: response }) => {
        // Destructing response
        const { data } = response;
        const {
          pricesWithoutVAT,
          subtotal,
          discountTotal,
          userDiscountTotal,
          userDiscount,
          total,
          subtotalTax,
          shippingTotal,
          shippingTax,
          totalVA,
          currency: orderCurrency,
        } = data;

        const symbol = findCurrencySymbol(orderCurrency, currencies);
        setFooterValues({
          subtotal,
          userDiscountPercentage: userDiscount,
          userDiscount: userDiscountTotal,
          total,
          vat: subtotalTax,
          pricesWithoutVAT,
          shipping: shippingTotal,
          taxProvider: shippingTax,
          totalVA,
          discountTotal,
          symbol,
        });
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchProductsList');
        console.log(err);
      });
  };
  useEffect(() => {
    // we need to make sure that it's only checking if there are products in the state
    if (productItems.length > 0) {
      checkOrderDetail();
    }
  }, [productItems, providerSelected, discountValue.discount]);

  // Check if shippingProviders are valid
  const shippingProviderIsValid = () => {
    let valid = false;
    // New array with valid shipping or undefined attr
    const shipping = providersList.map(
      s => s?.userClickAndCollectPrice?.price >= 0 || s?.userPrice?.price >= 0,
    );
    // if there is a shipping different of undefined, the prop will change to valid and the
    // shipping providers list will be rendered
    if (shipping.some(s => s !== undefined)) {
      valid = true;
    }
    return valid;
  };

  const checkPrice = item => {
    const alternative = item?.userClickAndCollectPrice
      ? Math.round(item?.userClickAndCollectPrice.price)
      : null;
    return item?.userPrice ? Math.round(item?.userPrice.price) : alternative;
  };

  const getVariationList = (selectedShippingAddress = null) => {
    setIsLoading(true);
    const url = '/api/market/product/available/variation/list';
    let country;
    // First check if there is shipping country
    if (shippingCountry) {
      country = shippingCountry;
    } else {
      // check if a shipping address was chosen, if not use billing address
      country = selectedShippingAddress
        ? selectedShippingAddress.countryCode
        : user?.address?.countryCode || null;
    }

    const params = {
      isPublic: true,
      country,
      currency: currency || 'eur',
      user: _id,
    };

    axios
      .get(url, {
        params,
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setIsLoading(false);
        setProductsSelector(response.data);
        // console.log('*PARAMS', params);
        // console.log('*PARAMS - Response', response.data.find(p => p._id === "6278ed87b104289302624868"));
        // console.log('*Response', response.data);
        // Reset state and list
        setProductItems([]);
        setFooterValues(initialStateFooter);
        setProvidersList([]);
        if (tableEl && tableEl.current) {
          tableEl.current.setList([]);
        }
      })
      .catch(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchProducts');
        console.log(err);
      });
  };

  const handleDifferentShippingAddress = () => {
    setShippingAddressIsDifferent(prevState => {
      if (prevState) {
        // In case of turning to false clean shippingAddress
        getVariationList();
        setState({ addressOption: '', providerOption: '' });
      }
      return !prevState;
    });
  };

  useEffect(() => {
    getVariationList();
  }, [state.addressOption]);

  return (
    <Modal onClose={onClose}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div id="makeOrderModalId" className="modal-content">
          <div
            id="modalBody"
            className="modal-body makeOrder-modal"
            style={{ maxHeight: '80vh', overflowY: 'auto' }}
          >
            <h5 className="modal-secondary-title">
              {t('sessionDetails_modal_newOrder')}
            </h5>
            <div className="m-invoice-2">
              <div className="m-invoice__wrapper">
                <div
                  id="customer-info-modal"
                  className="customer-info justify-content-center"
                >
                  <CustomerInfo
                    onShippingChange={handleShipping}
                    user={user}
                    shippingAddressIsDifferent={shippingAddressIsDifferent}
                    handleDifferentShippingAddress={
                      handleDifferentShippingAddress
                    }
                    isOnlyDigitalProducts={isOnlyDigitalProducts}
                  />
                </div>
                <div className="m-invoice__body">
                  <div className="table-responsive">
                    <List
                      ref={tableEl}
                      columns={columns}
                      id={tableId}
                      loading={false}
                      data={[]}
                      pagination={false}
                      onInit={handleListInitialization}
                      onDataUpdated={() => {}}
                    />
                  </div>
                  <div className="sessionDetail_productForm">
                    <div className="col-lg-12">
                      {!showNewProduct ? (
                        <div className="sessionDetail_add-product">
                          <button
                            type="button"
                            onClick={AddProduct}
                            className="btn m-btn--pill btn-metal navigation-button"
                          >
                            <i className="fa fa-plus" />
                          </button>
                          <span
                            style={{ alignSelf: 'center', marginLeft: '1rem' }}
                          >
                            {t('sessionDetails_addNewProduct')}
                          </span>
                        </div>
                      ) : (
                        <div className="row">
                          <div className="col-lg-6">
                            <FormInput
                              id="product"
                              value={productItems.id}
                              name="product"
                              disabled={false}
                              label={t('sessionDetails_modal.product')}
                              containerClassName="col-lg-10"
                              placeholder={t(
                                'sessionDetails_modal.productPlaceholder',
                              )}
                              groupProps={{ className: 'row' }}
                              type="select"
                              onChange={handleChangeProduct('product')}
                              inputProps={{
                                items: productsSelector.map(p => ({
                                  id: p._id,
                                  text: p.parentProduct
                                    ? `${p.sku} - ${p.name} (${p.parentProduct.name})`
                                    : `${p.sku} - ${p.name}`,
                                })),
                              }}
                            />
                          </div>
                          <div className="col-lg-4">
                            <FormInput
                              id="amount"
                              value={productItems.amount}
                              name="amount"
                              disabled={disabledField}
                              label={t('sessionDetails_modal.quantity')}
                              containerClassName="col-lg-9"
                              placeholder={t(
                                'sessionDetails_modal.quantityPlaceholder',
                              )}
                              groupProps={{ className: 'row' }}
                              type="number"
                              onChange={handleChangeProduct('amount')}
                              inputProps={{
                                min: 1,
                              }}
                            />
                          </div>
                          <div
                            style={{ textAlign: 'center' }}
                            className="col-lg-2"
                          >
                            <button
                              disabled={disabledField || checkTheStock}
                              onClick={addProduct}
                              type="button"
                              className="btn btn-dynamic"
                              style={{ width: '-webkit-fill-available' }}
                            >
                              {t('sessionDetails_modal_save')}
                            </button>
                          </div>
                        </div>
                      )}
                      {checkTheStock && (
                        <div className="col-lg-12 quantity-above-stockAvailable">
                          {t('order.create.errorMsg.quantityAboveStock')}
                        </div>
                      )}
                    </div>
                  </div>
                  {!isOnlyDigitalProducts && (
                    <>
                      {providersList.length > 0 && shippingProviderIsValid() ? (
                        <div className="m-invoice__items">
                          <div className="row">
                            {providersList.map((provider, i) => (
                              <div className="m-invoice__item shipping-options col-lg-3">
                                <div style={{ display: 'flex' }}>
                                  <div
                                    style={{ marginRight: '1rem' }}
                                    className="checkbox-shipping"
                                  >
                                    <label
                                      style={{ display: 'block' }}
                                      htmlFor={provider._id}
                                    >
                                      <span className="radio__input">
                                        <input
                                          id={provider._id}
                                          type="radio"
                                          name="provider"
                                          value={provider._id}
                                          checked={
                                            state.providerOption ===
                                            provider._id
                                          }
                                          onChange={handleProvider}
                                        />
                                        <span className="radio__control" />
                                      </span>
                                    </label>
                                  </div>
                                  <div>
                                    <span className="m-invoice__subtitle">
                                      {`${t(
                                        'academy.sessions.makeOrder.shippingProvider',
                                      )} ${i + 1}`}
                                    </span>
                                    <br />
                                    <span className="m-invoice__text">
                                      <strong>
                                        {`${provider.name} - ${formatPrice(
                                          checkPrice(provider),
                                          provider?.userPrice?.symbol ||
                                            provider.userClickAndCollectPrice
                                              ?.symbol,
                                        )}`}
                                      </strong>
                                      <br />
                                      {` (${t('shop.product.without.tax')})`}
                                      <br />
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <span className="customer-info-invalid">
                          {`${t(
                            'academy.sessions.makeOrder.shippingProvider.warning',
                          )}.`}
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="m-invoice__footer row">
                  <div className="m-invoice__table table-responsive col-6 order-session">
                    <table className="wc-order-totals">
                      <tbody>
                        {footerValues ? (
                          <tr>
                            <td className="label">
                              {t('sessionDetails_modal.subtotal')}
                              {` (${t('academy.sessions.makeOrder.noVAT')})`}
                              {': '}
                            </td>
                            <td width="2%" />
                            <td className="total">
                              <span className="Price-amount amount">
                                <span className="Price-currencySymbol">
                                  {formatPrice(
                                    footerValues.subtotal,
                                    footerValues.symbol,
                                  )}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {footerValues.discountTotal > 0 ? (
                          <tr>
                            <td className="label">
                              {t('sessionDetails_modal.discount')}
                              {': '}
                            </td>
                            <td width="1%" />
                            <td className="total">
                              <span className="Price-amount amount">
                                <span className="Price-currencySymbol">
                                  {`- ${formatPrice(
                                    footerValues.discountTotal,
                                    footerValues.symbol,
                                  )}`}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {user?.marketDirectDiscount > 0 ? (
                          <tr>
                            <td className="label">
                              {t('order_discount')}
                              {` (${user?.marketDirectDiscount}%): `}
                            </td>
                            <td width="1%" />
                            <td className="total">
                              <span className="Price-amount amount">
                                <span className="Price-currencySymbol">
                                  {`- ${formatPrice(
                                    footerValues.userDiscount || 0,
                                    footerValues.symbol,
                                  )}`}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {footerValues ? (
                          <tr>
                            <td className="label">
                              {t('sessionDetails_modal.vat')}
                              {': '}
                            </td>
                            <td width="1%" />
                            <td className="total">
                              <span className="Price-amount amount">
                                <span className="Price-currencySymbol">
                                  {formatPrice(
                                    footerValues.vat,
                                    footerValues.symbol,
                                  )}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {footerValues ? (
                          <tr>
                            <td className="label">
                              {`${t('sessionDetails_modal.shipping')}
                                ${
                                  footerValues.shipping > 0
                                    ? ` (${footerValues.taxProvider}%)`
                                    : ''
                                }:`}
                            </td>
                            <td width="1%" />
                            <td className="total">
                              <span className="Price-amount amount">
                                <span className="Price-currencySymbol">
                                  {formatPrice(
                                    footerValues.shipping,
                                    footerValues.symbol,
                                  )}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {footerValues ? (
                          <tr>
                            <td className="label">
                              {t('sessionDetails_modal.total')}
                              {` (${t('academy.sessions.makeOrder.VAT')})`}
                              {': '}
                            </td>
                            <td width="1%" />
                            <td className="total">
                              <span className="Price-amount amount">
                                <span className="Price-currencySymbol">
                                  {formatPrice(
                                    footerValues.total || 0,
                                    footerValues.symbol,
                                  )}
                                </span>
                              </span>
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                  <div className="check-coupon col-6">
                    <div className="form-group">
                      <div className="input-group">
                        <label
                          style={{
                            alignSelf: 'center',
                            paddingRight: '1rem',
                            margin: '0',
                          }}
                          htmlFor="coupon"
                        >
                          {t('sessionDetails_modal_coupon')}
                        </label>
                        <FormInput
                          id="discountCoupon"
                          value={discountCode}
                          onChange={handleChangeCoupon}
                          placeholder={t(
                            'sessionDetails_modal_coupon.placeholder',
                          )}
                          type="text"
                        />

                        <div className="input-group-append">
                          <button
                            className="btn btn-theme btn-dynamic"
                            type="button"
                            onClick={checkCouponCode}
                          >
                            {t('shop.cart.check')}
                          </button>
                        </div>
                      </div>
                    </div>
                    {errorMessage && (
                      <span className="customer-info-invalid">
                        {t('academy.session.createOrder.coins.error')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-buttons">
              <div className="row justify-content-end">
                <div className="col-lg-3 col-md-6 button-cancel">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    {t('sessionDetails_modal_cancel')}
                  </button>
                </div>
                <div className="col-lg-3 col-md-6">
                  <button
                    onClick={createOrder}
                    type="button"
                    className={`btn btn-dynamic
                      ${
                        isLoading
                          ? 'm-loader m-loader--right m-loader--light'
                          : ''
                      }`}
                    disabled={!disabled}
                  >
                    {t('sessionDetails_modal_placeOrder')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

MakeOrderModal.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  user: PropTypes.shape(UserProp.propType).isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
};
MakeOrderModal.defaultProps = {
  onClose: () => {},
};

export default compose(translate, withCSRF)(MakeOrderModal);
